import { webClient } from "@shared/apis"
import { Status } from "@shared/constants"
import { getParam, setTokens } from "@shared/modules"

export default {
  // 라이브 화면 우측 하단 노출용 숏폼 목록 조회
  getShortsList: (streamerId: number) =>
    webClient.get<ShortsListItem[]>(`/shorts/streamers/${streamerId}`),
  // 숏폼 상세 조회 (재생 페이지용)
  getShortsDetail: (shortsId: number) =>
    webClient.get<ShortsDetail>(`/shorts/${shortsId}`),
}

export type ShortsProgressStatus =
  | "WAITING"
  | "PROGRESSING"
  | "COMPLETED"
  | "CANCELED"
  | "FAILED"

// 숏폼 리스트 조회
export interface ShortsListItem {
  id: number | null
  title: string | null
  fileName: string | null
  thumbnail: string | null
  durationSeconds: number | null
  recordEndDate: string | null
  createdAt: string | null
  finalSummary: string | null
  progressStatus: ShortsProgressStatus | null
  streamUrl: string
}

// 특정 숏폼 디테일 조회
export interface ShortsDetail {
  id: number | null
  scheduleId: number | null
  nickname: string | null
  title: string | null
  fileName: string | null
  recordStartDate: string | null
  recordEndDate: string | null
  createdAt: string | null
  summaryStatus: string | null
  finalSummary: string | null
  progressStatus: ShortsProgressStatus | null
  streamUrl: string
}

// 와우넷 리다이렉트 진입 검증 응답
// role은 방송의 공개/무료 여부와 무관하게, 해당 숏폼을 볼 권한이 있는 사용자만 "AUTH"로 내려온다.
// 권한이 없으면 서버가 에러를 주거나 200에 "UNAUTH"를 줄 수 있으므로 두 경우 모두 처리해야 한다.
export interface ShortsValidateResult {
  userToken: string
  userid: string
  role: "AUTH" | "UNAUTH"
  nickname: string
  analid: string
  // 비회원(ticket만 있고 username이 없는 진입)일 때만 내려온다.
  // 회원은 기존처럼 check API에서 토큰을 받으므로 null.
  accessToken?: string
  chattingToken?: string
  vodWmsAuthToken?: string
}

/**
 * 현재 URL의 진입 정보로 숏폼 진입을 검증한다.
 * ticket은 withTicket일 때만 보낸다. (숏폼 재생 페이지 진입 시에만 필요)
 * 와우넷이 숏폼 진입 시 check용 ticket과 별도로 내려주는 secondticket을 쓴다. (check API에는 ticket을 그대로 사용)
 * OTP로 진입한 경우(어드민 등)는 OTP 교환 단계에서 인증이 끝났으므로 검증을 생략하고 null을 반환한다.
 */
export async function validateShortsEntry(
  shortsId: number,
  { withTicket = false }: { withTicket?: boolean } = {},
): Promise<ShortsValidateResult | null> {
  if (getParam("otp")) return null

  const { data } = await webClient.post<ShortsValidateResult>(
    `/shorts/${shortsId}/validate`,
    {
      username: getParam("username"),
      ...(withTicket && { ticket: getParam("secondticket") }),
      usernumber: getParam("usernumber"),
    },
  )

  return data
}

/**
 * 숏폼 비회원 진입 검증으로 받은 토큰을 세팅한다.
 * 회원은 check API에서 이미 토큰을 받으므로 응답에 토큰이 없고, 이때는 아무것도 하지 않는다.
 * refreshToken은 내려오지 않아 만료되면 갱신할 수 없다. (webClient 인터셉터에서 kick)
 * 라이브 화면의 요약 모달 검증에서는 호출하면 안 된다. 라이브 세션의 토큰을 덮어쓴다.
 */
export function setAnonymousShortsTokens(entry: ShortsValidateResult | null) {
  if (!entry?.accessToken) return

  webClient.defaults.headers["Authorization"] = entry.accessToken
  setTokens({
    accessToken: entry.accessToken,
    chattingToken: entry.chattingToken,
    vodWmsAuthToken: entry.vodWmsAuthToken,
  })
}

/**
 * validate 결과로 숏폼 시청 권한이 있는지 판단한다.
 * OTP 진입(null)은 검증을 생략했으므로 권한이 있는 것으로 본다.
 * 무료 시청자든, 공개 시청자든 권한이 있으면 role이 AUTH가 된다.
 */
export function hasShortsPermission(entry: ShortsValidateResult | null) {
  return !entry || entry.role === "AUTH"
}

export const SHORTS_NO_PERMISSION_MESSAGE =
  "서비스에 가입하셔야 시청하실 수 있습니다."

// 원래 에러 코드 다양하게 해서 세팅해야 할텐데 허허
// 리뉴얼 해야 해결이 될듯 ㅠㅠ
export function getShortsValidateErrorMessage(err: any): string {
  const status = err?.response?.status

  if (status === Status.NOT_FOUND) {
    return "존재하지 않는 숏폼입니다."
  }

  if (status === Status.FORBIDDEN) {
    return "부정 사용으로 판단되어 숏폼 내용 확인이 불가능합니다."
  }

  if (
    status === Status.BAD_REQUEST ||
    status === Status.INTERNAL_SERVER_ERROR
  ) {
    return "사용자 인증에 실패하였습니다."
  }

  return "일시적인 서버 오류가 발생했습니다."
}

export const SHORTS_EXPIRED_MESSAGE =
  "시청 정보가 만료되었습니다. 다시 접속해 주세요."

/**
 * 숏폼 재생 정보(getShortsDetail) 조회 실패 문구.
 * 진입 검증을 통과한 뒤의 403은 부정 사용이 아니라 토큰 만료이므로 문구를 구분한다.
 * 나머지 상태 코드는 진입 검증과 원인이 같아 그대로 쓴다.
 */
export function getShortsDetailErrorMessage(err: any): string {
  if (err?.response?.status === Status.FORBIDDEN) {
    return SHORTS_EXPIRED_MESSAGE
  }

  return getShortsValidateErrorMessage(err)
}
